<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-form-select
                            :options="form.quadrimestresAnos"
                            :validations="validations.quadrimestre"
                            @change="handleQuadrimestreAnoChange"
                            label="Ano"
                            show-default-option
                            v-model="model.quadrimestreAno"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="model.quadrimestreAno
                                ? form.quadrimestresAnos.find(a => a.value === model.quadrimestreAno).quadrimestres
                                : []"
                            :validations="validations.quadrimestre"
                            @change="handleQuadrimestreChange"
                            label="Quadrimestre"
                            show-default-option
                            v-model="model.quadrimestre"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.equipes"
                            @change="handleEquipeChange"
                            default-option-text="Todas"
                            label="Equipe"
                            show-default-option
                            text-field="descricao"
                            v-model="model.equipeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-multiselect
                            :options="form.boasPraticas"
                            :select-limit="null"
                            @update:dropdownClosed="handleBoasPraticasChange"
                            label="Boas práticas"
                            option-name-key="text"
                            option-text-selecteds="boa prática"
                            v-model="model.boasPraticas"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-form-checkbox
                            :label="false"
                            @change="handleBuscaAtivaChange"
                            v-model="model.buscaAtiva"
                        >
                            Busca ativa
                            <span
                                class="ml-1"
                                title="Exibe somente gestantes ou puérperas com pendência em uma ou mais boas práticas do indicador."
                                v-rkm-tooltip.hover
                            >
                                <rkm-icon icon="info-circle" />
                            </span>
                        </rkm-form-checkbox>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.sexos"
                            @change="handleSexoChange"
                            default-option-text="Todos"
                            label="Sexos"
                            show-default-option
                            text-field="descricao"
                            v-model="model.sexoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :validations="validations.idadeMinima"
                            @change="handleIdadeMinimaChange"
                            label="Idade mínima"
                            number
                            v-model="model.idadeMinima"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :validations="validations.idadeMaxima"
                            @change="handleIdadeMaximaChange"
                            label="Idade máxima"
                            number
                            v-model="model.idadeMaxima"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input-autocomplete
                            :disabled="disablePaciente"
                            :repository="repositories.relatorioCuidadoGestacaoPuerperio.paciente"
                            :request-query-string-params="pacienteSearchParams"
                            :search-fields="pacienteSearchFields"
                            text-field="nome"
                            label="Paciente"
                            placeholder="Nome, CNS, CPF ou Prontuário"
                            v-model="model.paciente"
                            @select="handlePacienteChange"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="resultado.equipe.itens.length > 0">
            <rkm-area
                v-if="totalizadores"
                no-margin-top
                title="Totalizadores"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            disabled
                            label="Total de pacientes"
                            v-model="totalizadores.totalPacientes"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            disabled
                            label="Total de equipes"
                            v-model="totalizadores.totalEquipes"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            disabled
                            label="Total de microáreas"
                            v-model="totalizadores.totalMicroareas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            disabled
                            label="Denominador"
                            v-model="totalizadores.denominador"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            disabled
                            label="Numerador"
                            v-model="totalizadores.numerador"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            disabled
                            label="Resultado"
                            :value="`${totalizadores.resultado}%`"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col v-if="model.boasPraticas.includes('A')">
                        <rkm-form-input
                            disabled
                            label="Boa prática A"
                            :value="`${totalizadores.boaPraticaA}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('B')">
                        <rkm-form-input
                            disabled
                            label="Boa prática B"
                            :value="`${totalizadores.boaPraticaB}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('C')">
                        <rkm-form-input
                            disabled
                            label="Boa prática C"
                            :value="`${totalizadores.boaPraticaC}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('D')">
                        <rkm-form-input
                            disabled
                            label="Boa prática D"
                            :value="`${totalizadores.boaPraticaD}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('E')">
                        <rkm-form-input
                            disabled
                            label="Boa prática E"
                            :value="`${totalizadores.boaPraticaE}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('F')">
                        <rkm-form-input
                            disabled
                            label="Boa prática F"
                            :value="`${totalizadores.boaPraticaF}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('G')">
                        <rkm-form-input
                            disabled
                            label="Boa prática G"
                            :value="`${totalizadores.boaPraticaG}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('H')">
                        <rkm-form-input
                            disabled
                            label="Boa prática H"
                            :value="`${totalizadores.boaPraticaH}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('I')">
                        <rkm-form-input
                            disabled
                            label="Boa prática I"
                            :value="`${totalizadores.boaPraticaI}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('J')">
                        <rkm-form-input
                            disabled
                            label="Boa prática J"
                            :value="`${totalizadores.boaPraticaJ}%`"
                        />
                    </rkm-col>

                    <rkm-col v-if="model.boasPraticas.includes('K')">
                        <rkm-form-input
                            disabled
                            label="Boa prática K"
                            :value="`${totalizadores.boaPraticaK}%`"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>
                    <rkm-button
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-table
                            :fields="equipeColumns"
                            :items="resultado.equipe.itens"
                            :sort-by="resultado.equipe.sortBy"
                            :sort-desc="resultado.equipe.sortDesc"
                            @sort-changed="onSortChangedEquipes"
                        >
                            <template v-slot:head(resultado)>
                                Resultado
                                <span
                                    title="Resultado calculado pela fórmula oficial do indicador com base no numerador, denominador e pontuação máxima."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(classificacaoResultado)>
                                Classificação
                                <span
                                    title="Faixa de desempenho da equipe conforme parâmetro do indicador."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaA)>
                                A
                                <span
                                    title="1ª consulta presencial com médica(o) ou enfermeira(o) até a 12ª semana de gestação."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaB)>
                                B
                                <span
                                    title="Pelo menos 7 consultas presenciais ou remotas com médica(o) ou enfermeira(o) durante a gestação."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaC)>
                                C
                                <span
                                    title="Pelo menos 7 aferições de pressão arterial durante a gestação."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaD)>
                                D
                                <span
                                    title="Pelo menos 7 registros simultâneos de peso e altura durante a gestação."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaE)>
                                E
                                <span
                                    title="Pelo menos 3 visitas domiciliares por ACS/TACS após a 1ª consulta do pré-natal."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaF)>
                                F
                                <span
                                    title="1 dose de dTpa adulto a partir da 20ª semana de gestação."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaG)>
                                G
                                <span
                                    title="Testes rápidos ou exames para sífilis, HIV, hepatite B e hepatite C no 1º trimestre."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaH)>
                                H
                                <span
                                    title="Testes rápidos ou exames para sífilis e HIV no 3º trimestre."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaI)>
                                I
                                <span
                                    title="Pelo menos 1 consulta no puerpério."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaJ)>
                                J
                                <span
                                    title="Pelo menos 1 visita domiciliar no puerpério."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:head(boaPraticaK)>
                                K
                                <span
                                    title="Pelo menos 1 atividade de saúde bucal durante a gestação."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>
                            </template>

                            <template v-slot:cell(resultado)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaA)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaB)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaC)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaD)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaE)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaF)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaG)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaH)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaI)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaJ)="{ value }">{{ value }}%</template>
                            <template v-slot:cell(boaPraticaK)="{ value }">{{ value }}%</template>
                        </rkm-table>

                        <rkm-area
                            v-if="model.buscaAtiva"
                            title="Busca ativa por paciente"
                        >
                            <template v-slot:trailing>
                                <span
                                    title="Exibe somente gestantes ou puérperas com pendência em uma ou mais boas práticas do indicador."
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="info-circle" />
                                </span>

                                <rkm-dropdown
                                    class="mr-2 ml-2"
                                    color="primary"
                                    cols="1"
                                    menu-class="auto-scroll"
                                    no-caret
                                    small
                                    title="Colunas"
                                    v-rkm-tooltip.hover
                                >
                                    <template v-slot:button-content>
                                        <rkm-icon icon="columns" />
                                    </template>

                                    <rkm-form-checkbox
                                        :indeterminate="resultado.paciente.indeterminate"
                                        @change="selectAllPacientes"
                                        class="mx-2"
                                        v-model="resultado.paciente.selectedAll"
                                    >
                                        Todas
                                    </rkm-form-checkbox>

                                    <rkm-form-checkbox-group
                                        :options="optionalColumnsPacientes"
                                        class="mx-2"
                                        stacked
                                        text-field="label"
                                        v-model="resultado.paciente.selectedCols"
                                        value-field="key"
                                    />
                                </rkm-dropdown>
                            </template>

                            <rkm-table
                                :fields="fieldsPacientes"
                                :items="resultado.paciente.itens"
                                :sort-by="resultado.paciente.sortBy"
                                :sort-desc="resultado.paciente.sortDesc"
                                @sort-changed="onSortChangedPacientes"
                            >
                                <template v-slot:head(boaPraticaA)>
                                    A
                                    <span
                                        title="1ª consulta presencial com médica(o) ou enfermeira(o) até a 12ª semana de gestação."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaB)>
                                    B
                                    <span
                                        title="Pelo menos 7 consultas presenciais ou remotas com médica(o) ou enfermeira(o) durante a gestação."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaC)>
                                    C
                                    <span
                                        title="Pelo menos 7 aferições de pressão arterial durante a gestação."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaD)>
                                    D
                                    <span
                                        title="Pelo menos 7 registros simultâneos de peso e altura durante a gestação."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaE)>
                                    E
                                    <span
                                        title="Pelo menos 3 visitas domiciliares por ACS/TACS após a 1ª consulta do pré-natal."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaF)>
                                    F
                                    <span
                                        title="1 dose de dTpa adulto a partir da 20ª semana de gestação."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaG)>
                                    G
                                    <span
                                        title="Testes rápidos ou exames para sífilis, HIV, hepatite B e hepatite C no 1º trimestre."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaH)>
                                    H
                                    <span
                                        title="Testes rápidos ou exames para sífilis e HIV no 3º trimestre."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaI)>
                                    I
                                    <span
                                        title="Pelo menos 1 consulta no puerpério."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaJ)>
                                    J
                                    <span
                                        title="Pelo menos 1 visita domiciliar no puerpério."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:head(boaPraticaK)>
                                    K
                                    <span
                                        title="Pelo menos 1 atividade de saúde bucal durante a gestação."
                                        v-rkm-tooltip.hover
                                    >
                                        <rkm-icon icon="info-circle" />
                                    </span>
                                </template>

                                <template v-slot:cell(boaPraticaA)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaB)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaC)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaD)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaE)="{ item }">
                                    <span v-if="item.areaTipoId === 5 && !item.boaPraticaE" class="badge badge-secondary">N/C</span>
                                    <rkm-icon
                                        v-else
                                        :class="{ green: item.boaPraticaE, red: !item.boaPraticaE }"
                                        :icon="item.boaPraticaE ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaF)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaG)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaH)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaI)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaJ)="{ item }">
                                    <span v-if="item.areaTipoId === 5 && !item.boaPraticaJ" class="badge badge-secondary">N/C</span>
                                    <rkm-icon
                                        v-else
                                        :class="{ green: item.boaPraticaJ, red: !item.boaPraticaJ }"
                                        :icon="item.boaPraticaJ ? 'check-circle' : 'times-circle'"
                                    />
                                </template>

                                <template v-slot:cell(boaPraticaK)="{ value }">
                                    <rkm-icon
                                        :class="{ green: value, red: !value }"
                                        :icon="value ? 'check-circle' : 'times-circle'"
                                    />
                                </template>
                            </rkm-table>
                        </rkm-area>
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </template>
    </div>
</template>

<script>
// Mixins.
import { data, error, file, repository, stringCase, formats } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

// Enum.
import sexo from '@/enums/cadastro/cidadao/sexo'

export default {
    computed: {
        validations() {
            const validations = {
                periodo: { required: this.model.quadrimestre === null },
                quadrimestre: { required: this.model.periodo.start === null || this.model.periodo.end === null },
                idadeMinima: {
                    max: 3,
                },
                idadeMaxima: {
                    max: 3,
                },
            }

            if (!this.isNullOrEmptyString(this.model.idadeMaxima)) {
                validations.idadeMinima.max_value = this.model.idadeMaxima
            }

            if (!this.isNullOrEmptyString(this.model.idadeMinima)) {
                validations.idadeMaxima.min_value = this.model.idadeMinima
            }

            return validations
        },
        disablePaciente() {
            if (!this.hasPeriodo && this.model.quadrimestre === null) {
                return true
            }

            return !this.isNullOrEmptyString(this.model.idadeMinima)
                && !this.isNullOrEmptyString(this.model.idadeMaxima)
                && (this.model.idadeMinima > this.model.idadeMaxima
                    || this.model.idadeMaxima < this.model.idadeMinima)
        },
        pacienteSearchParams() {
            return this.camelObjectKeysToSnakeRecursive({
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                unidadeId: this.model.unidadeId,
                equipeId: this.model.equipeId,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
                sexoId: this.model.sexoId,
                idadeMinima: this.model.idadeMinima,
                idadeMaxima: this.model.idadeMaxima,
            })
        },
        hasPeriodo() {
            return this.model.periodo.start !== null && this.model.periodo.end !== null
        },
        optionalColumnsPacientes() {

            return this.pacienteColumns.filter(c => !(c.fixed || false))
        },
        fieldsPacientes() {

            return this.pacienteColumns.filter(c => (c.fixed || false) || this.resultado.paciente.selectedCols.includes(c.key))
        },
        equipeColumns() {
            const columns = [
                { key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true },
                { key: 'equipeDescricao', label: 'Equipe', sortable: true },
                { key: 'denominador', label: 'Denominador', sortable: true },
                { key: 'numerador', label: 'Numerador', sortable: true },
                { key: 'resultado', label: 'Resultado', sortable: true },
                { key: 'classificacaoResultado', label: 'Classificação', sortable: true },
            ]

            if (this.model.boasPraticas.includes('A')) {
                columns.push({ key: 'boaPraticaA', label: 'A', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('B')) {
                columns.push({ key: 'boaPraticaB', label: 'B', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('C')) {
                columns.push({ key: 'boaPraticaC', label: 'C', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('D')) {
                columns.push({ key: 'boaPraticaD', label: 'D', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('E')) {
                columns.push({ key: 'boaPraticaE', label: 'E', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('F')) {
                columns.push({ key: 'boaPraticaF', label: 'F', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('G')) {
                columns.push({ key: 'boaPraticaG', label: 'G', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('H')) {
                columns.push({ key: 'boaPraticaH', label: 'H', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('I')) {
                columns.push({ key: 'boaPraticaI', label: 'I', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('J')) {
                columns.push({ key: 'boaPraticaJ', label: 'J', sortable: true, class: 'text-center text-nowrap' })
            }
            if (this.model.boasPraticas.includes('K')) {
                columns.push({ key: 'boaPraticaK', label: 'K', sortable: true, class: 'text-center text-nowrap' })
            }

            return columns
        },
        pacienteColumns() {
            const columns = [
                { key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true, fixed: true },
                { key: 'equipeDescricao', label: 'Equipe', sortable: true, fixed: true },
                { key: 'pacienteNome', label: 'Paciente', sortable: true, fixed: true },
                { key: 'sexoDescricao', label: 'Sexo', sortable: true, fixed: true },
                { key: 'idade', label: 'Idade', sortable: true, fixed: true },
                { key: 'microareaDescricao', label: 'Microárea', sortable: true, fixed: true },
                { key: 'usuarioCadastroNome', label: 'Responsável pelo cadastro', sortable: true, fixed: true },
                { key: 'dataNascimento', label: 'Data de nascimento', formatter: v => v ? this.formatDate(v) : null, filterByFormatted: true, sortable: true },
                { key: 'telefonePrincipal', label: 'Telefone', sortable: true },
                { key: 'endereco', label: 'Endereço', sortable: true },
            ]

            if (this.model.boasPraticas.includes('A')) {
                columns.push({ key: 'boaPraticaA', label: 'A', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('B')) {
                columns.push({ key: 'boaPraticaB', label: 'B', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('C')) {
                columns.push({ key: 'boaPraticaC', label: 'C', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('D')) {
                columns.push({ key: 'boaPraticaD', label: 'D', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('E')) {
                columns.push({ key: 'boaPraticaE', label: 'E', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('F')) {
                columns.push({ key: 'boaPraticaF', label: 'F', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('G')) {
                columns.push({ key: 'boaPraticaG', label: 'G', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('H')) {
                columns.push({ key: 'boaPraticaH', label: 'H', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('I')) {
                columns.push({ key: 'boaPraticaI', label: 'I', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('J')) {
                columns.push({ key: 'boaPraticaJ', label: 'J', class: 'text-center text-nowrap', fixed: true })
            }
            if (this.model.boasPraticas.includes('K')) {
                columns.push({ key: 'boaPraticaK', label: 'K', class: 'text-center text-nowrap', fixed: true })
            }

            columns.push({ key: 'pontuacaoTotal', label: 'Pontuação total', sortable: true, class: 'text-center', fixed: true })

            return columns
        },
    },
    created() {
        this.repositories = {
            relatorioCuidadoGestacaoPuerperio: this.getConectaSusRepository('atencaoBasica.relatorioCuidadoGestacaoPuerperio'),
            impressao: this.getConectaSusRepository('impressao.atencaoBasica'),
            exportacao: this.getConectaSusRepository('exportacao.atencaoBasica'),
        }

        this.load()
    },
    data() {
        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            pacienteSearchFields: [
                { key: 'cidadao.nome', searchable: true },
                { key: 'cidadao.nomeSocial', searchable: true },
                { key: 'cidadao.cns', searchable: true },
                { key: 'cidadao.cpf', searchable: true },
                { key: 'cidadao.data_nascimento', searchable: true },
                { key: 'paciente.prontuario', searchable: true },
            ],
            resultado: {
                equipe: {
                    itens: [],
                    sortBy: 'unidadeNomeFantasia',
                    sortDesc: false,
                },
                paciente: {
                    itens: [],
                    sortBy: 'pacienteNome',
                    sortDesc: false,
                    selectedAll: false,
                    indeterminate: false,
                    selectedCols: [],
                },
            },
            totalizadores: null,
            unwatchers: {
                unwatchPeriodo: null,
                unwatchPaciente: null,
                unwatchIdadeMinima: null,
                unwatchIdadeMaxima: null,
            },
            enums: { sexo },
            others: { moment },
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioCuidadoGestacaoPuerperio.create()
                .then(({ data }) => {
                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {
            this.unwatchers.unwatchPeriodo = this.$watch('model.periodo', {
                deep: true,
                handler() {
                    this.handlePeriodoChange()
                },
            })

            this.unwatchers.unwatchIdadeMinima = this.$watch('model.idadeMinima', () => this.clearResult())
            this.unwatchers.unwatchIdadeMaxima = this.$watch('model.idadeMaxima', () => this.clearResult())
            this.unwatchers.unwatchPaciente = this.$watch('model.paciente', () => this.clearResult())
        },
        handlePeriodoChange() {
            this.form.unidades = []
            this.form.equipes = []
            this.form.sexos = []
            this.model.quadrimestreAno = null
            this.model.quadrimestre = null
            this.model.unidadeId = null
            this.model.equipeId = null
            this.model.sexoId = null
            this.model.paciente = null

            this.clearResult()

            if (this.model.periodo.start === null || this.model.periodo.end === null)  {
                return
            }

            this.loadFromUnidades()
        },
        handleQuadrimestreAnoChange() {
            this.removeWatchers()
            this.model.periodo = { start: null, end: null }
            this.addWatchers()
            this.form.unidades = []
            this.form.equipes = []
            this.form.sexos = []
            this.model.quadrimestre = null
            this.model.unidadeId = null
            this.model.equipeId = null
            this.model.sexoId = null
            this.model.paciente = null

            this.clearResult()
        },
        handleQuadrimestreChange() {
            this.removeWatchers()
            this.model.periodo = { start: null, end: null }
            this.addWatchers()
            this.form.unidades = []
            this.form.equipes = []
            this.form.sexos = []
            this.model.unidadeId = null
            this.model.equipeId = null
            this.model.sexoId = null
            this.model.paciente = null

            this.clearResult()

            if (this.model.quadrimestre === null) {
                return
            }

            this.loadFromUnidades()
        },
        handleUnidadeChange() {
            this.form.equipes = []
            this.form.sexos = []
            this.model.equipeId = null
            this.model.sexoId = null
            this.model.paciente = null

            this.clearResult()
            this.loadFromEquipes()
        },
        handleEquipeChange() {
            this.form.sexos = []
            this.model.sexoId = null
            this.model.paciente = null

            this.clearResult()
            this.loadFromSexos()
        },
        handleBoasPraticasChange() {
            this.clearResult()
        },
        handleBuscaAtivaChange() {
            this.clearResult()
        },
        handleSexoChange() {
            this.model.paciente = null

            this.clearResult()
        },
        handleIdadeMinimaChange() {
            this.model.paciente = null

            this.clearResult()
        },
        handleIdadeMaximaChange() {
            this.model.paciente = null

            this.clearResult()
        },
        handlePacienteChange() {
            this.clearResult()
        },
        loadFromUnidades() {
            const params = {
                listar: 'unidades',
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
            }

            this.setLoading()

            this.repositories.relatorioCuidadoGestacaoPuerperio.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.unidades = data.unidades
                    this.form.equipes = data.equipes
                    this.form.sexos = data.sexos

                    this.selectDefaultSexo()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromEquipes() {
            const params = {
                listar: 'equipes',
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                unidadeId: this.model.unidadeId,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
            }

            this.setLoading()

            this.repositories.relatorioCuidadoGestacaoPuerperio.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.equipes = data.equipes
                    this.form.sexos = data.sexos

                    this.selectDefaultSexo()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromSexos() {
            const params = {
                listar: 'sexos',
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                unidadeId: this.model.unidadeId,
                equipeId: this.model.equipeId,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
            }

            this.setLoading()

            this.repositories.relatorioCuidadoGestacaoPuerperio.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.sexos = data.sexos

                    this.selectDefaultSexo()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        selectDefaultSexo() {
            if (this.form.sexos.find(s => s.id === this.enums.sexo.FEMININO)) {
                this.model.sexoId = this.enums.sexo.FEMININO
            }
        },
        clearResult() {
            this.resultado.equipe.itens = []
            this.resultado.paciente.itens = []
            this.totalizadores = null
        },
        search() {
            const params = {
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                unidadeId: this.model.unidadeId,
                equipeId: this.model.equipeId,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
                sexoId: this.model.sexoId,
                idadeMinima: this.model.idadeMinima,
                idadeMaxima: this.model.idadeMaxima,
                pacienteId: this.model.paciente?.id ?? null,
            }

            this.setLoading()

            this.repositories.relatorioCuidadoGestacaoPuerperio.index(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.resultado.equipe.itens = data.equipes
                    this.resultado.paciente.itens = data.pacientes
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.removeWatchers()

            this.form.unidades = []
            this.form.equipes = []
            this.form.sexos = []
            this.model.periodo = { start: null, end: null }
            this.model.quadrimestreAno = null
            this.model.quadrimestre = null
            this.model.unidadeId = null
            this.model.equipeId = null
            this.model.boasPraticas = this.form.boasPraticas.map(bp => bp.value)
            this.model.buscaAtiva = false
            this.model.sexoId = null
            this.model.idadeMinima = this.additionals.idadeMinima
            this.model.idadeMaxima = this.additionals.idadeMaxima
            this.model.paciente = null

            this.addWatchers()
            this.clearResult()

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        removeWatchers() {
            this.unwatchers.unwatchPeriodo()
            this.unwatchers.unwatchIdadeMinima()
            this.unwatchers.unwatchIdadeMaxima()
            this.unwatchers.unwatchPaciente()
        },
        print() {
            this.setLoading()

            const params = {
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                unidadeId: this.model.unidadeId,
                equipeId: this.model.equipeId,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
                sexoId: this.model.sexoId,
                idadeMinima: this.model.idadeMinima,
                idadeMaxima: this.model.idadeMaxima,
                pacienteId: this.model.paciente?.id ?? null,
                colunasPacientes: this.fieldsPacientes.map(f => this.camelToSnake(f.key)),
                equipeSortBy: this.camelToSnake(this.resultado.equipe.sortBy),
                equipeSortDesc: this.resultado.equipe.sortDesc,
                pacienteSortBy: this.camelToSnake(this.resultado.paciente.sortBy),
                pacienteSortDesc: this.resultado.paciente.sortDesc,
            }

            this.repositories.impressao.relatorioCuidadoGestacaoPuerperio(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            const params = {
                periodo: this.hasPeriodo ? {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                } : null,
                quadrimestreAno: this.model.quadrimestreAno,
                quadrimestre: this.model.quadrimestre,
                unidadeId: this.model.unidadeId,
                equipeId: this.model.equipeId,
                boasPraticas: this.model.boasPraticas,
                buscaAtiva: this.model.buscaAtiva,
                sexoId: this.model.sexoId,
                idadeMinima: this.model.idadeMinima,
                idadeMaxima: this.model.idadeMaxima,
                pacienteId: this.model.paciente?.id ?? null,
                colunasPacientes: this.fieldsPacientes.map(f => this.camelToSnake(f.key)),
                equipeSortBy: this.camelToSnake(this.resultado.equipe.sortBy),
                equipeSortDesc: this.resultado.equipe.sortDesc,
                pacienteSortBy: this.camelToSnake(this.resultado.paciente.sortBy),
                pacienteSortDesc: this.resultado.paciente.sortDesc,
            }

            this.repositories.exportacao.relatorioCuidadoGestacaoPuerperio(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        onSortChangedEquipes(context) {
            this.resultado.equipe.sortBy = context.sortBy
            this.resultado.equipe.sortDesc = context.sortDesc
        },
        onSortChangedPacientes(context) {
            this.resultado.paciente.sortBy = context.sortBy
            this.resultado.paciente.sortDesc = context.sortDesc
        },
        selectAllPacientes(selected) {

            this.resultado.paciente.selectedCols = selected ? this.optionalColumnsPacientes.map(c => c.key) : []
        },
    },
    mixins: [data, error, file, repository, stringCase, formats],
    watch: {
        'resultado.paciente.selectedCols'(newValue) {

            this.resultado.paciente.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumnsPacientes.length
            this.resultado.paciente.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumnsPacientes.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
.green {
    color: green;
}
.red {
    color: red;
}
</style>